import { ReuseModal } from "../../ReuseModal";
import { Box, Divider, Stack, Typography } from "@mui/material";
import { ReuseInput } from "../../ReuseInput";
import { FileList } from "../../FileList";
import { FileDropZone } from "../../FileDropZone";
import React, { useState } from "react";
import { URL_REGEX } from "../../../../utils/regex";
import { AlertInCircleIcon } from "../../../images";
import { ReuseTooltip } from "../../ReuseTooltip";
import { CustomButton } from "../../CustomButton";
import { toast } from "react-toastify";
import { CustomToast } from "../../CustomToast";
import CancelIcon from "@mui/icons-material/Cancel";
import { CustomChip } from "../../CustomChip";
import { CIDAddedType } from "../../../../types/apiTypes";

type Props = {
  cid: CIDAddedType;
  isOpen: boolean;
  onClose: () => void;
  onSubmit: (cid: CIDAddedType) => void;
};

export const UploadHgModal = ({ cid, isOpen, onClose, onSubmit }: Props) => {
  const [uploadedFiles, setUploadedFiles] = useState<File[]>(cid.files);
  const [links, setLinks] = useState<string[]>(cid.urls);

  const [url, setUrl] = useState<string>("");
  const [isError, setIsError] = useState<boolean>(false);

  const submitHandler = () => {
    onSubmit({ ...cid, urls: links, files: uploadedFiles });
  };

  const inputChangeHandler = (field: string, value: string) => {
    setIsError(!!value && !URL_REGEX.test(value));
    setUrl(value);
  };

  const changeFileHandler = (fileName: string, file: File) => {
    setUploadedFiles((state) =>
      state.map((el) => {
        if (el.name !== fileName) return el;
        return file;
      }),
    );
  };

  const removeFileHandler = (fileName: string) => {
    setUploadedFiles((state) => state.filter(({ name }) => name !== fileName));
  };

  const onFileUpload = (files: File[]) => {
    setUploadedFiles((prev) => [...prev, ...files]);
  };

  const addUrlHandler = () => {
    setLinks((prev) => {
      if (prev.includes(url.trim())) {
        toast.error(
          <CustomToast
            type="error"
            title="Invalid Url"
            text="Each url must be unique. Please choose a different url."
          />,
        );
        return prev;
      }

      setUrl("");
      return [...prev, url.trim()];
    });
  };

  const removeLinkHandler = (link: string) => {
    setLinks((prev) => prev.filter((el) => el !== link));
  };

  return (
    <ReuseModal
      isOpen={isOpen}
      onClose={onClose}
      title={"Upload HG Insights"}
      onSubmit={submitHandler}
      maxWidth="540px"
      submitBtnColor="#1C3C6C"
      submitBtnText={"Save"}
    >
      <Stack gap={"12px"}>
        <Typography
          component="span"
          variant="subtitle1"
          color="custom.textDarkBlue"
          fontWeight={500}
        >
          Upload Data
        </Typography>
        <Stack gap="16px">
          <FileList
            isDisabled={false}
            files={uploadedFiles}
            removeFileHandler={removeFileHandler}
            changeFileHandler={changeFileHandler}
            acceptedExtensions={["csv", "xls", "xlsx"]}
          />
          {uploadedFiles.length === 0 && (
            <FileDropZone
              uploadedFiles={uploadedFiles}
              onFileUpload={onFileUpload}
              acceptedExtensions={["csv", "xls", "xlsx"]}
              label="Drag your CSV file here"
              maxFiles={5}
              customTitle={
                "Drag your CSV file here  (up to 5 files, 50 MB max each)"
              }
            />
          )}

          <Box
            sx={{
              display: "flex",
              alignItems: "center",
              width: "100%",
            }}
          >
            <Divider sx={{ flexGrow: 1, color: "#CFCFCF" }} />
            <Typography
              variant="button"
              sx={{
                marginX: "8px",
                color: "#4C545B",
              }}
            >
              and/or
            </Typography>
            <Divider sx={{ flexGrow: 1, color: "#CFCFCF" }} />
          </Box>

          <Stack gap={"30px"}>
            <Stack flexDirection={"row"} gap={"8px"} alignItems={"center"}>
              <Typography
                component="span"
                variant="subtitle1"
                color="custom.textDarkBlue"
                fontWeight={500}
              >
                Import via Link
              </Typography>
              <ReuseTooltip
                text={"The file should be publicity accessible."}
                maxWidth={"184px"}
              >
                <AlertInCircleIcon
                  width={"20px"}
                  height={"20px"}
                  color={"#1C3C6C"}
                />
              </ReuseTooltip>
            </Stack>
            <Stack gap={"12px"}>
              <Stack flexDirection={"row"} gap={"12px"} paddingBottom={"24px"}>
                <ReuseInput
                  name="product_url"
                  handleInputChange={inputChangeHandler}
                  errorMessage={
                    <Stack component="span" alignItems="left" width="100%">
                      <span>
                        Invalid URL: Please check the format and try again.
                      </span>
                    </Stack>
                  }
                  value={url}
                  label="HG Insights URL"
                  placeholder="HG Insights URL"
                  isError={isError}
                  isDisabled={links.length === 5}
                />
                <ReuseTooltip
                  text={
                    links.length === 5 &&
                    "You’ve reached the maximum of 5 links."
                  }
                  hideChildren={false}
                  maxWidth={"138px"}
                >
                  <CustomButton
                    onClick={addUrlHandler}
                    variant="outlined"
                    customColor="#FFFFFF"
                    textColor="#1D3E70"
                    type="button"
                    customSize="fit"
                    disabled={!url || isError || links.length === 5}
                    sx={{
                      height: "40px",
                      borderColor: "#1D3E70",
                      padding: "8px 16px",

                      "&.Mui-disabled": {
                        backgroundColor: "transparent",
                        borderColor: "#EEEEEE",
                      },
                    }}
                  >
                    Add Link
                  </CustomButton>
                </ReuseTooltip>
              </Stack>
              {links.map((link) => (
                <CustomChip
                  key={link}
                  label={link}
                  onDelete={() => removeLinkHandler(link)}
                  disabled={false}
                  sx={{
                    backgroundColor: "transparent",
                    border: "1px solid #BCBCBC",
                    fontSize: "16px",
                    lineHeight: "24px",
                    fontWeight: 500,
                    color: "#232323",
                  }}
                  deleteIcon={
                    <CancelIcon
                      sx={{
                        color: "#617798",
                        width: "20px",
                        height: "20px",
                        marginRight: "6px",
                      }}
                    />
                  }
                />
              ))}
            </Stack>
          </Stack>
        </Stack>
      </Stack>
    </ReuseModal>
  );
};
